import { Controller, useFormContext } from 'react-hook-form'
import { useMemo } from 'react'
import clsx from 'clsx'

import { Checkbox, CheckboxGroup } from '@wartek-id/checkbox-v1'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'

import { IndicatorCard } from 'app/KinerjaPage/pages/PelaksanaanV2/components/IndicatorCard'
import { useAPIPelaksanaanTargetPerilaku } from 'api/kinerja/useAPIKinerja'
import { GPK_PEGAWAI_PRAKTIK_KINERJA_ICONS } from 'app/KinerjaGpkPage/constants/contents'
import { assetUrl } from 'utils/url'

import type {
  IPelaksanaanDocumentPreparationForm,
  ResponsePelaksanaanProgress,
} from 'types/kinerja-pelaksanaan'

interface TargetPerilakuFormProps {
  disabled: boolean
  pelaksanaanProgressData?: ResponsePelaksanaanProgress
  isLoadingPelaksanaanProgress: boolean
}

export const TargetPerilakuForm = ({
  disabled,
  pelaksanaanProgressData,
  isLoadingPelaksanaanProgress,
}: TargetPerilakuFormProps) => {
  const { data: behaviorData, isLoading: behaviorLoading } =
    useAPIPelaksanaanTargetPerilaku(pelaksanaanProgressData?.contentId, {
      enabled: !!pelaksanaanProgressData?.contentId,
      staleTime: 30 * 60 * 1000,
    })

  const { control, register, formState } =
    useFormContext<IPelaksanaanDocumentPreparationForm>()

  const formErrors = formState.errors

  // -----------------------------------------
  // Resolve Praktik Kinerja asset path
  // -----------------------------------------
  const illustrationAssetPath = useMemo(() => {
    const icon =
      GPK_PEGAWAI_PRAKTIK_KINERJA_ICONS[
        pelaksanaanProgressData?.subIndicatorName
      ]

    if (icon) return assetUrl(`/illustrations/${icon}`)

    return ''
  }, [pelaksanaanProgressData?.subIndicatorName])

  return (
    <div className="p-6">
      <Text variant="heading-sm" className="mb-2">
        Berdasarkan perencanaan Anda:
      </Text>
      <IndicatorCard
        illustrationAssetPath={illustrationAssetPath}
        subIndicatorName={pelaksanaanProgressData?.subIndicatorName}
        subIndicatorDefinition={pelaksanaanProgressData?.subIndicatorDefinition}
        qualityTarget={pelaksanaanProgressData?.qualityTarget}
        contentId={pelaksanaanProgressData?.contentId}
        isLoading={isLoadingPelaksanaanProgress}
      />
      <Text variant="action" className="mb-2 mt-12">
        Apa perilaku spesifik yang ingin Anda pelajari dan tampilkan pada
        Observasi Praktik Kinerja?
      </Text>
      <Text color="subdued" className="mb-1">
        Target Perilaku (berdasarkan indikator pilihan Anda)
      </Text>
      <Controller
        control={control}
        name="behavioralTargets"
        rules={{
          required: 'Wajib dipilih',
        }}
        render={({ field }) => {
          const errorMessage = formErrors.behavioralTargets
          const isInvalid = !!errorMessage

          return (
            <div className="space-y-1">
              <CheckboxGroup
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                className="flex flex-row gap-4"
              >
                {behaviorLoading && (
                  <>
                    <Shimmer width={260} height={120} />
                    <Shimmer width={260} height={120} />
                    <Shimmer width={260} height={120} />
                  </>
                )}
                {behaviorData?.data?.map((thisBehavior, index) => {
                  const targetSegments = /(.+?):(.+)/.exec(
                    thisBehavior.behavioralTarget
                  )

                  let title: string | undefined
                  let description = thisBehavior.behavioralTarget
                  if (targetSegments) {
                    title = targetSegments[1].trim()
                    description = targetSegments[2].trim()
                  }

                  return (
                    <Checkbox
                      key={`behavior_${index}`}
                      name={field.name}
                      value={thisBehavior.id}
                      labelPosition="start"
                      className={clsx(
                        'p-3 border rounded w-72 max-w-full',
                        isInvalid ? '!border-red-400' : 'border-subdued'
                      )}
                      disabled={disabled}
                    >
                      {title ? (
                        <>
                          <strong>{title}:</strong>
                          <br />
                          <span>{description}</span>
                        </>
                      ) : (
                        <span>{description}</span>
                      )}
                    </Checkbox>
                  )
                })}
              </CheckboxGroup>
              {isInvalid && (
                <Text
                  variant="helper"
                  color="critical"
                  id="behavioral-targets-error"
                >
                  Wajib dipilih
                </Text>
              )}
            </div>
          )
        }}
      />
      <div className="mt-12 space-y-2 w-8/12 max-w-full">
        <Text variant="action">
          Apa saja upaya yang akan Anda lakukan untuk mempelajari Target
          Perilaku?
        </Text>
        <div className="space-y-1">
          <Text color="subdued" as="label" htmlFor="upaya-mempelajari">
            Upaya Mempelajari
          </Text>
          <Textarea
            isDisabled={disabled}
            maxLength={250}
            rows={3}
            placeholder="Ketik di sini"
            id="upaya-mempelajari"
            {...register('behavioralTargetEffort', {
              required: {
                value: true,
                message: 'Wajib diisi',
              },
              maxLength: 250,
            })}
            aria-describedby="behavioral-target-effort-error"
            isInvalid={!!formErrors.behavioralTargetEffort}
            errorMessage={formErrors.behavioralTargetEffort?.message}
          />
        </div>
      </div>
    </div>
  )
}
